<script>
	// New goods-return certificate: pick the customer, mark how many of each model they return.
	// Every model they bought in the last two years is listed at the last price they paid
	// (after the line and general discounts, before VAT). Saved in this app only, not sent to Verifone.
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import fetchData from '../../api.js';
	import UserStore from '../../stores/user';
	import { VAT_PERCENT } from '../../lib/format';
	import AppShell from '../../components/AppShell.svelte';
	import CustomerPicker from '../../components/CustomerPicker.svelte';
	import Icon from '../../components/Icon.svelte';

	// what was marked so far stays on the tablet per customer until the certificate is saved
	const DRAFT_KEY = 'agents_return_draft:';

	let customer = null;
	let picking = false;
	let loading = false;
	let progress = null;
	let error = '';
	let offline = false;
	let models = [];
	let qty = {};
	let remarks = '';
	let cartons = 0;
	let q = '';
	let onlyChosen = false;
	let confirming = false;
	let saving = false;
	let saveError = '';
	let requestNo = 0;

	const price = (n) => '₪' + (Number(n) || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	const day = (d) => (d ? d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(2, 4) : '');
	const round2 = (n) => Math.round(n * 100) / 100;

	$: chosen = models.filter((m) => (qty[m.code] || 0) > 0);
	$: units = chosen.reduce((s, m) => s + qty[m.code], 0);
	$: total = round2(chosen.reduce((s, m) => s + round2(qty[m.code] * m.price), 0));
	$: vat = round2((total * VAT_PERCENT) / 100);
	$: needle = q.trim().toLowerCase();
	$: shown = (onlyChosen ? chosen : models).filter((m) => !needle || [m.code, m.name].some((v) => String(v || '').toLowerCase().includes(needle)));
	$: if (onlyChosen && !chosen.length) onlyChosen = false;
	$: cartonsOk = Number.isInteger(cartons) && cartons >= 1 && cartons <= 999;

	function readDraft() {
		try {
			const d = JSON.parse(localStorage.getItem(DRAFT_KEY + customer.CustomerNo) || '{}') || {};
			qty = d.qty || {};
			remarks = d.remarks || '';
			cartons = d.cartons || 0;
		} catch (e) {
			qty = {};
			remarks = '';
			cartons = 0;
		}
	}

	function saveDraft() {
		try {
			const any = Object.values(qty).some((n) => n > 0) || remarks.trim() || cartons;
			if (any) localStorage.setItem(DRAFT_KEY + customer.CustomerNo, JSON.stringify({ qty, remarks, cartons }));
			else localStorage.removeItem(DRAFT_KEY + customer.CustomerNo);
		} catch (e) {
			/* storage blocked */
		}
	}

	function setQty(code, value) {
		const n = Math.max(0, Math.floor(Number(value)) || 0);
		qty = { ...qty, [code]: n };
		saveDraft();
	}

	async function load() {
		const mine = ++requestNo;
		loading = true;
		error = '';
		offline = false;
		models = [];
		progress = null;
		// the server copies a few invoices per call; ask again until all are in
		for (let i = 0; i < 100; i++) {
			const res = await fetchData({ action: 'agents_return_models', token: $UserStore.data, customer_no: customer.CustomerNo });
			if (mine !== requestNo) return;
			if (!res || Number(res.success) !== 1) {
				error = (res && res.msg) || 'אין חיבור לשרת. בדוק את האינטרנט ונסה שוב.';
				break;
			}
			progress = { docs: Number(res.docs) || 0, done: (Number(res.docs) || 0) - (Number(res.pending) || 0) };
			if (res.models) {
				models = res.models;
				offline = !!res.offline;
				break;
			}
		}
		loading = false;
	}

	function pick(c) {
		picking = false;
		customer = c;
		q = '';
		onlyChosen = false;
		readDraft();
		load();
	}

	function setCartons(value) {
		cartons = Math.min(999, Math.max(0, Math.floor(Number(value)) || 0));
		saveDraft();
	}

	async function save() {
		saveError = '';
		if (!cartonsOk) {
			saveError = 'יש לרשום בכמה קרטונים הסחורה';
			return;
		}
		saving = true;
		const res = await fetchData({
			action: 'agents_return_save',
			token: $UserStore.data,
			customer_no: customer.CustomerNo,
			customer_name: customer.CustomerName,
			remarks: remarks.trim(),
			cartons,
			items: chosen.map((m) => ({ model: m.code, qty: qty[m.code] }))
		});
		saving = false;
		if (res && Number(res.success) === 1 && res.id) {
			qty = {};
			remarks = '';
			cartons = 0;
			saveDraft();
			push('/agents/return-notes/' + res.id);
		} else {
			saveError = (res && res.msg) || 'השמירה נכשלה. נסה שוב.';
		}
	}

	onMount(() => {
		if (!$UserStore || !$UserStore.data) {
			push('/login');
			return;
		}
		picking = true;
	});
</script>

<AppShell title="תעודת החזרה חדשה">
	<section class="panel">
		<a class="back" href="#/agents/return-notes">→ לכל התעודות</a>
		<div class="field">
			<span class="label">לקוח</span>
			{#if customer}
				<button class="customer" on:click={() => (picking = true)}>
					<Icon name="customer" size={22} />
					<b>{customer.CustomerName}</b>
					<small class="muted">#{customer.CustomerNo}</small>
					<span class="change">החלפה</span>
				</button>
			{:else}
				<button class="btn-primary" on:click={() => (picking = true)}><Icon name="customer" size={22} /> בחירת לקוח</button>
			{/if}
		</div>
	</section>

	{#if !customer}
		<div class="empty-state"><b>בוחרים לקוח</b>ואז מסמנים כמה יחידות מכל דגם הוא מחזיר.</div>
	{:else if loading}
		<div class="loading">
			<div class="spinner" />
			<b>מביא את הדגמים שהלקוח קנה…</b>
			{#if progress && progress.docs}<span class="muted">{progress.done} מתוך {progress.docs} חשבוניות</span>{/if}
			<small class="muted">בפעם הראשונה ללקוח זה לוקח קצת זמן. בפעם הבאה זה מיידי.</small>
		</div>
	{:else if error}
		<div class="alert-error">{error}</div>
		<button class="btn-ghost retry" on:click={load}>לנסות שוב</button>
	{:else}
		{#if offline}<div class="alert-warn">ווריפון לא ענה. מוצגים רק דגמים מחשבוניות שכבר נקראו קודם.</div>{/if}

		<div class="tools">
			<label class="search model-search">
				<span class="icon"><Icon name="search" size={22} /></span>
				<input type="search" placeholder="חיפוש דגם: מספר או שם" bind:value={q} />
				{#if q}<button class="clear" on:click={() => (q = '')} aria-label="ניקוי"><Icon name="close" size={20} /></button>{/if}
			</label>
			{#if chosen.length}
				<div class="seg">
					<button class:on={!onlyChosen} on:click={() => (onlyChosen = false)}>כל הדגמים</button>
					<button class:on={onlyChosen} on:click={() => (onlyChosen = true)}>נבחרו ({chosen.length})</button>
				</div>
			{/if}
		</div>
		<p class="muted note">
			{models.length} דגמים שהלקוח קנה בשנתיים האחרונות. המחיר הוא המחיר האחרון ששילם ליחידה, אחרי כל ההנחות, לפני מע״מ.
		</p>

		{#if !shown.length}
			<div class="empty-state">
				<b>{models.length ? 'אין דגם כזה' : 'לא נמצאו חשבוניות ללקוח בשנתיים האחרונות'}</b>
				{models.length ? 'אפשר לחפש לפי מספר דגם או חלק מהשם.' : ''}
			</div>
		{:else}
			<div class="models">
				{#each shown as m (m.code)}
					<div class="model" class:on={(qty[m.code] || 0) > 0}>
						<div class="who">
							<b dir="ltr">{m.code}</b>
							<span class="name">{m.name}</span>
							<small class="muted">נקנה לאחרונה {day(m.date)} · חשבונית {m.doc_no}</small>
						</div>
						<div class="unit">
							<b>{price(m.price)}</b>
							<small class="muted">ליחידה</small>
						</div>
						<div class="stepper">
							<button on:click={() => setQty(m.code, (qty[m.code] || 0) - 1)} disabled={!qty[m.code]} aria-label="פחות"><Icon name="minus" size={20} /></button>
							<input type="number" inputmode="numeric" min="0" value={qty[m.code] || 0} on:focus={(e) => e.target.select()} on:change={(e) => setQty(m.code, e.target.value)} />
							<button on:click={() => setQty(m.code, (qty[m.code] || 0) + 1)} aria-label="עוד"><Icon name="plus" size={20} /></button>
						</div>
					</div>
				{/each}
			</div>
		{/if}

		{#if chosen.length}
			<div class="bar-spacer" />
			<button class="save-bar" on:click={() => (confirming = true)}>
				<span class="what">
					<b>תעודת החזרה · {customer.CustomerName}</b>
					<small>{chosen.length === 1 ? 'דגם אחד' : chosen.length + ' דגמים'} · {units} יח׳ · {price(total)} לפני מע״מ</small>
				</span>
				<span class="go">המשך לשמירה</span>
			</button>
		{/if}
	{/if}
</AppShell>

{#if picking}
	<CustomerPicker on:close={() => (picking = false)} on:select={(e) => pick(e.detail)} />
{/if}

{#if confirming && customer}
	<div class="backdrop over-sheet" on:click={() => (confirming = false)} />
	<div class="modal confirm" role="dialog" aria-label="שמירת תעודת החזרה">
		<div class="modal-head">
			<h2>תעודת החזרה · {customer.CustomerName}</h2>
			<button class="icon-btn" on:click={() => (confirming = false)} aria-label="סגירה"><Icon name="close" /></button>
		</div>
		<div class="modal-body">
			{#each chosen as m (m.code)}
				<div class="citem">
					<span class="cname"><b dir="ltr">{m.code}</b> {m.name}</span>
					<span>{qty[m.code]} × {price(m.price)}</span>
					<b>{price(round2(qty[m.code] * m.price))}</b>
				</div>
			{/each}
			<div class="totals">
				<div><span>סה״כ לפני מע״מ ({units} יח׳)</span><b>{price(total)}</b></div>
				<div><span>מע״מ {VAT_PERCENT}%</span><span>{price(vat)}</span></div>
				<div class="grand"><span>סה״כ לזיכוי כולל מע״מ</span><b>{price(round2(total + vat))}</b></div>
			</div>
			<div class="cartons" class:missing={!cartonsOk}>
				<span>כמה קרטונים? <b>חובה</b></span>
				<div class="stepper">
					<button on:click={() => setCartons(cartons - 1)} disabled={cartons <= 0} aria-label="פחות"><Icon name="minus" size={20} /></button>
					<input type="number" inputmode="numeric" min="1" max="999" value={cartons || ''} placeholder="0" on:focus={(e) => e.target.select()} on:input={(e) => setCartons(e.target.value)} />
					<button on:click={() => setCartons(cartons + 1)} aria-label="עוד"><Icon name="plus" size={20} /></button>
				</div>
			</div>
			<label class="remarks">
				<span>הערות (לא חובה)</span>
				<textarea rows="2" maxlength="500" bind:value={remarks} on:input={saveDraft} placeholder="למשל: סיבת ההחזרה" />
			</label>
			<p class="muted small">התעודה נשמרת בתוכנה בלבד ולא נשלחת לווריפון. אחרי השמירה אפשר להדפיס אותה.</p>
			{#if saveError}<div class="alert-error">{saveError}</div>{/if}
		</div>
		<div class="foot">
			<button class="btn-ghost" on:click={() => (confirming = false)}>חזרה לדגמים</button>
			<button class="btn-primary" on:click={save} disabled={saving || !cartonsOk}>{saving ? 'שומר…' : 'שמירת תעודה'}</button>
		</div>
	</div>
{/if}

<style>
	.panel { display: flex; gap: 24px; flex-wrap: wrap; align-items: flex-end; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 16px 20px; margin-bottom: 16px; }
	.back { align-self: center; color: var(--ink-2); font-size: 16px; text-decoration: underline; min-height: 44px; display: flex; align-items: center; }
	.field { display: flex; flex-direction: column; gap: 6px; }
	.label { font-size: 14px; color: var(--muted); }
	.customer { display: flex; align-items: center; gap: 10px; height: 56px; padding: 0 16px; border: 1px solid var(--line-strong); border-radius: 14px; background: var(--surface); font-size: 18px; max-width: 520px; }
	.customer b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.change { margin-inline-start: 8px; color: var(--ink-2); text-decoration: underline; font-size: 15px; }
	.loading { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 20px; text-align: center; }
	.loading .spinner { margin: 20px auto 8px; }
	.retry { margin-top: 12px; }
	.alert-warn { background: var(--warn-bg); color: var(--warn); border-radius: 12px; padding: 12px 16px; margin-bottom: 12px; }
	.tools { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
	.model-search { background: var(--surface); max-width: 560px; }
	.note { margin: 10px 0 16px; font-size: 15px; }
	.models { display: flex; flex-direction: column; gap: 8px; }
	.model { display: grid; grid-template-columns: 1fr 130px auto; gap: 16px; align-items: center; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 12px 16px 12px 12px; }
	.model.on { border-color: var(--ok); box-shadow: inset 0 0 0 1px var(--ok); background: var(--ok-bg); }
	.who { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
	.who b { font-size: 18px; text-align: right; }
	.who .name { font-size: 16px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.who small { font-size: 13px; }
	.unit { display: flex; flex-direction: column; align-items: flex-end; }
	.unit b { font-size: 20px; }
	.unit small { font-size: 13px; }
	.stepper { display: flex; align-items: center; border: 1px solid var(--line-strong); border-radius: 12px; overflow: hidden; background: var(--surface); }
	.stepper button { width: 52px; height: 52px; border: 0; background: var(--bg); display: grid; place-items: center; }
	.stepper button:disabled { opacity: .35; }
	.stepper input { width: 60px; height: 52px; border: 0; text-align: center; font-size: 19px; font-weight: 700; -moz-appearance: textfield; }
	.stepper input::-webkit-inner-spin-button { -webkit-appearance: none; }
	.bar-spacer { height: 96px; }
	.save-bar {
		position: fixed; z-index: 19; inset-inline: 0; margin: 0 auto; width: min(760px, calc(100vw - 32px));
		bottom: calc(var(--tabbar-h) + env(safe-area-inset-bottom) + 12px);
		display: flex; align-items: center; gap: 14px; height: 68px; padding: 0 12px 0 18px;
		border: 0; border-radius: 16px; background: var(--ink); color: #fff; box-shadow: var(--shadow); text-align: start;
	}
	.save-bar .what { flex: 1; min-width: 0; display: flex; flex-direction: column; }
	.save-bar .what b { font-size: 17px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.save-bar .what small { font-size: 14px; opacity: .8; }
	.save-bar .go { background: #fff; color: var(--ink); border-radius: 12px; padding: 0 16px; height: 46px; display: grid; place-items: center; font-weight: 700; font-size: 16px; flex: none; }
	.confirm { width: min(760px, 94vw); display: flex; flex-direction: column; }
	.citem { display: grid; grid-template-columns: 1fr auto 110px; gap: 12px; align-items: center; padding: 10px 0; border-bottom: 1px solid var(--line); font-size: 16px; }
	.citem b:last-child { text-align: left; }
	.cname { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.totals { display: flex; flex-direction: column; gap: 6px; padding: 14px 0 8px; }
	.totals div { display: flex; justify-content: space-between; font-size: 16px; }
	.totals .grand { font-size: 19px; border-top: 1px solid var(--line); padding-top: 8px; }
	.cartons { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding: 12px 14px; border-radius: 12px; background: var(--bg); }
	.cartons > span { font-size: 17px; font-weight: 700; }
	.cartons > span b { font-size: 13px; color: var(--muted); font-weight: 500; margin-inline-start: 6px; }
	.cartons.missing { background: var(--warn-bg); }
	.cartons.missing > span b { color: var(--warn); }
	.remarks { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
	.remarks span { font-size: 15px; color: var(--ink-2); }
	.remarks textarea { border: 1px solid var(--line-strong); border-radius: 12px; padding: 10px 14px; font-size: 17px; font-family: inherit; resize: vertical; }
	.small { font-size: 14px; }
	.foot { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--line); }
	@media (max-width: 700px) {
		.model { grid-template-columns: 1fr auto; }
		.stepper { grid-column: 1 / -1; justify-self: start; }
	}
</style>
